import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import { Button, Chip, Icon, IconButton, Stack, Text, useRovingTabindex, tokens } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';
import type { IconName } from '@/design';
import { AddLayerDrawer } from './AddLayerDrawer';
import { LayerPreviewSwatch } from './LayerPreviewSwatch';
import { SavedViewsPanel } from './SavedViewsPanel';
import {
  applyAlternateSwitchVisibility,
  buildAlternateGroups,
  groupMatchesQuery,
  isAlternateGroupVisible,
  resolveActiveMemberId,
} from './alternateGroupModel';
import { emitAlternateSwitch } from './alternateGroupEvents';
import {
  layerStatuses,
  providerIcon,
  providerIdFromLayer,
  providerLabel,
  isLayerRestricted,
} from './layerModel';
import { LayerHealthDot, type LayerHealthState } from './LayerHealthDot';

interface LayerPanelProps {
  onFlyToLayer?(layer: Layer): void;
  onSetOpacity?(layerId: string, opacity: number): void;
  layerHealth?: Readonly<Record<string, LayerHealthState>>;
}

function kindIcon(kind: Layer['kind']): 'layers' | 'map' | 'terrain' | 'timeline' {
  if (kind === 'raster') return 'map';
  if (kind === 'vector') return 'layers';
  if (kind === 'timeseries') return 'timeline';
  return 'terrain';
}

export function LayerPanel({ onFlyToLayer, onSetOpacity, layerHealth = {} }: LayerPanelProps) {
  const catalog = useViewerStore((state) => state.catalog);
  const visible = useViewerStore((state) => state.visibleLayerIds);
  const setVisible = useViewerStore((state) => state.setVisible);
  const setManyVisible = useViewerStore((state) => state.setManyVisible);
  const selection = useViewerStore((state) => state.selection);
  const slotMap = useViewerStore((state) => state.slotMap);
  const setSelection = useViewerStore((state) => state.setSelection);
  /**
   * WP-2 scene-selection setter: keeps the chrome-side selection (the
   * one the Vector ToolCard listens to) in sync with the engine-side
   * pick. Without this the ToolCard would not light up when the user
   * picks a row in the layer manager.
   */
  const selectSceneByLayerId = useViewerStore((state) => state.selectByLayerId);
  const setPermissionDeniedMessage = useViewerStore((state) => state.setPermissionDeniedMessage);
  const saveCurrentView = useViewerStore((state) => state.saveCurrentView);
  const savedViews = useViewerStore((state) => state.savedViews);
  const restoreSavedView = useViewerStore((state) => state.restoreSavedView);
  const removeSavedView = useViewerStore((state) => state.removeSavedView);
  const [query, setQuery] = useState('');
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [activeAlternateByGroup, setActiveAlternateByGroup] = useState<Map<string, string>>(
    () => new Map(),
  );
  // M8 — opacity state for overlay layers
  const [layerOpacities, setLayerOpacities] = useState<Record<string, number>>({});
  const closeAddDrawer = useCallback(() => setAddDrawerOpen(false), []);
  
  // M8 — check if a layer is in the overlays slot and should show opacity controls
  const isOverlayLayer = useCallback((layer: Layer): boolean => {
    const slot = slotMap[layer.id];
    return slot === 'overlays' && layer.kind === '3dtiles';
  }, [slotMap]);
  
  // M8 — handle opacity slider changes
  const handleOpacityChange = useCallback((layerId: string, opacity: number) => {
    setLayerOpacities(prev => ({ ...prev, [layerId]: opacity }));
    // TODO(M8): Wire to engine adapter once onSetOpacity prop is connected from TwinShell
    onSetOpacity?.(layerId, opacity);
    console.log(`[LayerPanel] Opacity changed for ${layerId}: ${opacity}`);
  }, [onSetOpacity]);

  const alternateGroups = useMemo(() => buildAlternateGroups(catalog), [catalog]);

  useEffect(() => {
    setActiveAlternateByGroup((previous) => {
      const next = new Map(previous);
      for (const group of alternateGroups) {
        if (group.members.length <= 1) {
          next.set(group.groupKey, group.primary.id);
          continue;
        }
        if (!next.has(group.groupKey)) {
          next.set(
            group.groupKey,
            resolveActiveMemberId(group, visible, next),
          );
        }
      }
      return next;
    });
  }, [alternateGroups, visible]);

  const visibleLayers = useMemo(
    () => catalog.filter((layer) => visible.has(layer.id)),
    [catalog, visible],
  );
  const mixedCrs = useMemo(() => {
    const crsSet = new Set(visibleLayers.map((layer) => layer.crs));
    return crsSet.size > 1;
  }, [visibleLayers]);

  const filteredGroups = useMemo(
    () => alternateGroups.filter((group) => groupMatchesQuery(group, query)),
    [alternateGroups, query],
  );

  const roving = useRovingTabindex({
    count: filteredGroups.length,
    orientation: 'vertical',
    onActivate: (index) => {
      const group = filteredGroups[index];
      if (!group) return;
      const activeLayerId = resolveActiveMemberId(group, visible, activeAlternateByGroup);
      const activeLayer = group.members.find((member) => member.layer.id === activeLayerId)?.layer
        ?? group.primary;
      setSelection({ layerId: activeLayer.id });
      selectSceneByLayerId(activeLayer.id, {
        layerKind: activeLayer.kind,
        source: 'scene-tree',
      });
      if (activeLayer.bbox) onFlyToLayer?.(activeLayer);
    },
  });

  const statusRows = useMemo(() => {
    const now = new Date();
    const counts = new Map<string, { icon: IconName; label: string; count: number }>();
    for (const layer of catalog) {
      const statuses = layerStatuses(layer, { mixedCrs, now });
      for (const status of statuses) {
        const prev = counts.get(status.key);
        if (prev) {
          prev.count += 1;
        } else {
          counts.set(status.key, {
            icon: status.icon,
            label: status.label,
            count: 1,
          });
        }
      }
    }
    return [...counts.values()].sort((left, right) => right.count - left.count);
  }, [catalog, mixedCrs]);

  const selectGroupLayer = useCallback((layer: Layer) => {
    setSelection({ layerId: layer.id });
    selectSceneByLayerId(layer.id, {
      layerKind: layer.kind,
      source: 'scene-tree',
    });
    if (layer.bbox) onFlyToLayer?.(layer);
  }, [onFlyToLayer, selectSceneByLayerId, setSelection]);

  const toggleGroupVisibility = useCallback((group: ReturnType<typeof buildAlternateGroups>[number]) => {
    const activeLayerId = resolveActiveMemberId(group, visible, activeAlternateByGroup);
    const activeLayer = group.members.find((member) => member.layer.id === activeLayerId)?.layer
      ?? group.primary;
    const restricted = isLayerRestricted(activeLayer);
    if (restricted) {
      setPermissionDeniedMessage(
        `Layer "${activeLayer.label ?? activeLayer.id}" is restricted in this organization.`,
      );
      return;
    }

    const currentlyVisible = isAlternateGroupVisible(group, visible, activeLayerId);
    if (currentlyVisible) {
      setManyVisible(group.members.map((member) => member.layer.id), false);
      return;
    }

    for (const update of applyAlternateSwitchVisibility(group, undefined, activeLayerId, true)) {
      setVisible(update.layerId, update.visible);
    }
  }, [
    activeAlternateByGroup,
    setManyVisible,
    setPermissionDeniedMessage,
    setVisible,
    visible,
  ]);

  const switchAlternate = useCallback((
    group: ReturnType<typeof buildAlternateGroups>[number],
    nextActiveId: string,
  ) => {
    const previousActiveId = resolveActiveMemberId(group, visible, activeAlternateByGroup);
    if (previousActiveId === nextActiveId) return;

    setActiveAlternateByGroup((previous) => {
      const next = new Map(previous);
      next.set(group.groupKey, nextActiveId);
      return next;
    });

    const groupVisible = isAlternateGroupVisible(group, visible, previousActiveId);
    const updates = applyAlternateSwitchVisibility(
      group,
      previousActiveId,
      nextActiveId,
      groupVisible,
    );
    for (const update of updates) {
      setVisible(update.layerId, update.visible);
    }

    // Phase 2 / RFC-108-G — emit the engine crossfade event. The store's
    // `setVisible` path emits this for store-driven switches; the explicit
    // emit here covers UI-driven switches that bypass the store mutex
    // (e.g. legacy AppShell dropdown). Listener is idempotent — multiple
    // emits within the same animation frame collapse to one fade.
    if (groupVisible) {
      emitAlternateSwitch({
        groupKey: group.groupKey,
        fromLayerId: previousActiveId,
        toLayerId: nextActiveId,
        groupBecomingVisible: false,
      });
    }

    const nextLayer = group.members.find((member) => member.layer.id === nextActiveId)?.layer;
    if (nextLayer) {
      selectGroupLayer(nextLayer);
    }
  }, [
    activeAlternateByGroup,
    selectGroupLayer,
    setVisible,
    visible,
  ]);

  return (
    <section
      className="layer-panel"
      aria-label="Layer manager"
      style={{
        display: 'grid',
        gridTemplateRows: 'auto auto auto 1fr auto',
        minHeight: 0,
      }}
    >
      <header style={{ padding: tokens.space[2], borderBottom: tokens.border.thin }}>
        <Stack direction="row" align="center" gap={2}>
          <Text variant="subtitle" as="h2" style={{ margin: 0 }}>
            Layers
          </Text>
          <Chip icon="layers" label={`${visibleLayers.length} visible`} variant="neutral" />
          <span style={{ flex: '1 1 auto' }} />
          <Button variant="ghost" onClick={() => setAddDrawerOpen(true)} aria-label="Add layer">
            <Icon name="add" size={20} decorative />
            <span>Add layer</span>
          </Button>
        </Stack>
      </header>

      <div style={{ padding: tokens.space[2], borderBottom: tokens.border.thin }}>
        <label style={{ display: 'grid', gap: tokens.space[1] }}>
          <Text variant="caption" as="span">
            Search, geocode, and filter
          </Text>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by label, id, kind, provider"
            style={{
              minHeight: tokens.density.touchMin,
              borderRadius: tokens.radius.input,
              border: tokens.border.thin,
              background: tokens.color.surface[2],
              color: tokens.color.ink.primary,
              paddingInline: tokens.space[2],
            }}
          />
        </label>
      </div>

      <div style={{ padding: tokens.space[2], borderBottom: tokens.border.thin }}>
        <Text variant="caption" as="h3" style={{ margin: 0, color: tokens.color.ink.secondary }}>
          Layer health
        </Text>
        <Stack direction="row" gap={1} style={{ flexWrap: 'wrap', marginTop: tokens.space[1] }}>
          {statusRows.length === 0 ? (
            <Chip icon="check_circle" label="No active warnings" variant="success" />
          ) : (
            statusRows.map((status) => (
              <Chip
                key={status.label}
                icon={status.icon}
                label={`${status.label} (${status.count})`}
                variant="warning"
              />
            ))
          )}
        </Stack>
      </div>

      <div role="tree" aria-label="Layer rows" style={{ minHeight: 0, overflowY: 'auto' }}>
        <Stack direction="column" gap={1} style={{ padding: tokens.space[2] }}>
          {filteredGroups.map((group, index) => {
            const activeLayerId = resolveActiveMemberId(group, visible, activeAlternateByGroup);
            const activeLayer = group.members.find((member) => member.layer.id === activeLayerId)?.layer
              ?? group.primary;
            const statuses = layerStatuses(activeLayer, { mixedCrs, now: new Date() });
            const providerId = providerIdFromLayer(activeLayer);
            const isVisible = isAlternateGroupVisible(group, visible, activeLayerId);
            const isSelected = selection?.layerId === activeLayer.id
              || group.members.some((member) => member.layer.id === selection?.layerId);
            const hasAlternates = group.members.length > 1;
            const activeLabel = group.members.find((member) => member.layer.id === activeLayerId)?.alternateLabel
              ?? activeLayer.label
              ?? activeLayer.id;
            const isOverlay = isOverlayLayer(activeLayer);
            const currentOpacity = layerOpacities[activeLayer.id] ?? 0.6;

            return (
              <div
                key={group.groupKey}
                role="treeitem"
                aria-selected={isSelected}
                aria-label={group.displayLabel}
                style={{
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                  padding: tokens.space[2],
                  background: isSelected ? tokens.color.surface[3] : tokens.color.surface[2],
                }}
              >
                <Stack direction="row" align="center" gap={2}>
                  <button
                    type="button"
                    onClick={() => selectGroupLayer(activeLayer)}
                    style={{
                      appearance: 'none',
                      border: 0,
                      background: 'transparent',
                      color: 'inherit',
                      textAlign: 'left',
                      padding: 0,
                      flex: '1 1 auto',
                      minWidth: 0,
                      cursor: 'pointer',
                    }}
                    {...roving.getItemProps(index)}
                  >
                    <Stack direction="row" align="center" gap={2}>
                      <LayerPreviewSwatch layer={activeLayer} />
                      <Stack direction="column" gap={1} style={{ minWidth: 0 }}>
                        <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
                          <LayerHealthDot health={layerHealth[activeLayer.id] ?? 'loading'} />
                          <Icon name={kindIcon(activeLayer.kind)} size={20} decorative />
                          <Text variant="body" as="span">
                            {group.displayLabel}
                          </Text>
                          {hasAlternates ? (
                            <label
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: tokens.space[1],
                                marginLeft: tokens.space[1],
                              }}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
                                Alternate
                              </Text>
                              <select
                                aria-label={`Select alternate for ${group.displayLabel}`}
                                value={activeLayerId}
                                onChange={(event) => switchAlternate(group, event.target.value)}
                                style={{
                                  minHeight: tokens.density.touchMin,
                                  borderRadius: tokens.radius.input,
                                  border: tokens.border.thin,
                                  background: tokens.color.surface[1],
                                  color: tokens.color.ink.primary,
                                  paddingInline: tokens.space[1],
                                }}
                              >
                                {group.members.map((member) => (
                                  <option key={member.layer.id} value={member.layer.id}>
                                    {member.alternateLabel}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : null}
                        </Stack>
                        <Stack direction="row" gap={1} style={{ flexWrap: 'wrap' }}>
                          <Chip icon={providerIcon(providerId)} label={providerLabel(providerId)} variant="neutral" />
                          <Chip icon="language" label={activeLayer.crs} variant="neutral" />
                          {hasAlternates ? (
                            <Chip icon="layers" label={activeLabel} variant="neutral" />
                          ) : null}
                          {statuses.slice(0, 3).map((status) => (
                            <Chip
                              key={`${group.groupKey}-${status.key}`}
                              icon={status.icon}
                              label={status.detail ? `${status.label}: ${status.detail}` : status.label}
                              variant={status.variant}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </Stack>
                  </button>
                  <IconButton
                    aria-label={isVisible ? `Hide ${group.displayLabel}` : `Show ${group.displayLabel}`}
                    onClick={() => toggleGroupVisibility(group)}
                  >
                    <Icon name={isVisible ? 'visibility' : 'visibility_off'} size={20} decorative />
                  </IconButton>
                  {/* M8 — opacity slider for overlay layers */}
                  {isOverlay && isVisible ? (
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: tokens.space[1],
                        minWidth: '100px',
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Text variant="caption" as="span" style={{ fontSize: '11px', minWidth: '30px' }}>
                        {Math.round(currentOpacity * 100)}%
                      </Text>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={currentOpacity}
                        onChange={(event) => handleOpacityChange(activeLayer.id, parseFloat(event.target.value))}
                        aria-label="Layer opacity"
                        style={{
                          flex: '1',
                          height: '4px',
                          borderRadius: '2px',
                          background: tokens.color.surface[3],
                          outline: 'none',
                          appearance: 'none',
                        }}
                      />
                    </label>
                  ) : null}
                </Stack>
              </div>
            );
          })}
          {filteredGroups.length === 0 ? (
            <div
              style={{
                border: tokens.border.thin,
                borderRadius: tokens.radius.md,
                padding: tokens.space[2],
                background: tokens.color.surface[2],
              }}
            >
              <Stack direction="row" align="center" gap={2}>
                <Icon name="search" size={20} decorative />
                <Text variant="body" as="span">
                  No matching layers.
                </Text>
              </Stack>
            </div>
          ) : null}
        </Stack>
      </div>

      <div style={{ padding: tokens.space[2], borderTop: tokens.border.thin }}>
        <Stack direction="row" align="center" gap={2}>
          <Text variant="caption" as="h3" style={{ margin: 0, color: tokens.color.ink.secondary }}>
            Saved views
          </Text>
          <span style={{ flex: '1 1 auto' }} />
          <Button variant="ghost" onClick={() => saveCurrentView()}>
            <Icon name="bookmark" size={20} decorative />
            <span>Save view</span>
          </Button>
        </Stack>
        <Stack direction="column" gap={1} style={{ marginTop: tokens.space[2] }}>
          {savedViews.length === 0 ? (
            <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
              Save the current view to restore it later.
            </Text>
          ) : (
            savedViews.map((view) => (
              <div key={view.id} style={{ border: tokens.border.thin, borderRadius: tokens.radius.input }}>
                <Stack direction="row" align="center" gap={1} style={{ padding: tokens.space[1] }}>
                  <Button variant="ghost" onClick={() => restoreSavedView(view.id)}>
                    <Icon name="bookmark" size={20} decorative />
                    <span>{view.name}</span>
                  </Button>
                  <span style={{ flex: '1 1 auto' }} />
                  <IconButton aria-label={`Delete saved view ${view.name}`} onClick={() => removeSavedView(view.id)}>
                    <Icon name="delete" size={20} decorative />
                  </IconButton>
                </Stack>
              </div>
            ))
          )}
        </Stack>
      </div>

      <AddLayerDrawer open={addDrawerOpen} onClose={closeAddDrawer} />
      
      {/* Saved Views Panel - added below existing LayerPanel */}
      <SavedViewsPanel />
    </section>
  );
}
